import IconLocation from '@mui/icons-material/Place'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { PPDialog } from 'components/PPDialog'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PostDestination } from 'shared'
import { LocationInfo } from 'shared/types'
import { InstagramLocationSelector } from '../../../CustomizePostBlock/InstagramLocationSelector'
import styles from './InstagramLocationPicker.pcss'

// Keeps the picker below the composer chrome, and below the location dropdown popper (z-index 212).
const DIALOG_Z_INDEX = 211

interface InstagramLocationPickerProps {
  profiles: PostDestination[]
  selectedLocations: Record<string, LocationInfo | undefined>
  className?: string
  onSelectedLocationsChange: (selection: Record<string, LocationInfo | undefined>, applyToAll?: boolean) => void
}

export function InstagramLocationPicker(props: InstagramLocationPickerProps) {
  const intl = useIntl()
  const [selection, setSelection] = useState<Record<string, LocationInfo | undefined>>(props.selectedLocations)
  const [applyToAll, setApplyToAll] = useState(false)
  const [popupOpen, setPopupOpen] = useState(false)

  const locationEnabledProfiles = props.profiles.filter(p => !p.instagramDirect)

  useEffect(() => {
    setSelection(props.selectedLocations)
  }, [props.selectedLocations])

  const updateSelectedLocation = (location: LocationInfo | undefined, ppid: string) => {
    setSelection(current => ({
      ...current,
      [ppid]: location
    }))
  }

  const toggleApplyToAll = () => {
    setApplyToAll(!applyToAll)
  }

  const openPopup = () => {
    setPopupOpen(true)
  }

  const closePopup = () => {
    setPopupOpen(false)
    setApplyToAll(false)
  }

  const confirmSelection = () => {
    props.onSelectedLocationsChange(selection, applyToAll)
    closePopup()
  }

  // NOTE: if no profiles with location enabled, do not render the component
  if (locationEnabledProfiles.length === 0) {
    return null
  }

  const emptyLocationLabel = intl.formatMessage({ id: 'composer.labels.instagram.empty-location' })
  const singleLocationLabel = intl.formatMessage({ id: 'label.generic.location' })
    + `: ${props.selectedLocations[locationEnabledProfiles[0].ppid]?.name || 'none'}`
  const label = locationEnabledProfiles.length === 1
    ? singleLocationLabel || emptyLocationLabel
    : Object.values(props.selectedLocations).filter(Boolean).length > 0 ? (
      <FormattedMessage id={'composer.labels.instagram.locations-selected'} />
    ) : emptyLocationLabel

  return (
    <div className={props.className} data-testid="instagram-location-picker">
      <Button startIcon={<IconLocation fontSize="small" className={styles['icon-location']} />} onClick={openPopup}>
        {label}
      </Button>
      <PPDialog
        open={popupOpen}
        onClose={closePopup}
        size="lg"
        zIndex={DIALOG_Z_INDEX}
        title={<FormattedMessage id="composer.labels.select-multiple-locations" />}
        footer={
          <div className={styles.actions}>
            <div className={styles.cb}>
              <FormControlLabel
                label={<FormattedMessage id="general.labels.apply-to-all" />}
                classes={{ label: styles.label }}
                control={(
                  <Checkbox
                    checked={applyToAll}
                    color="primary"
                    size="small"
                    onChange={toggleApplyToAll}
                  />
                )}
              />
            </div>
            <Button onClick={closePopup}>
              <FormattedMessage id="actions.cancel" />
            </Button>
            <Button color="primary" variant="contained" onClick={confirmSelection}>
              <FormattedMessage id="actions.confirm" />
            </Button>
          </div>
        }
      >
        <div className={styles['popup-body']}>
          <div className={styles['albums-popup-content']}>
            {locationEnabledProfiles.map(profile => (
              <div className={styles['option-box']} key={profile.id}>
                <InstagramLocationSelector
                  profile={profile}
                  selectedLocation={selection[profile.ppid]}
                  onSelectedLocationChange={updateSelectedLocation}
                />
              </div>
            ))}
          </div>
          {locationEnabledProfiles.length > 0 && locationEnabledProfiles.length < props.profiles.length && (
            <div className={styles['info-text']}>
              <FormattedMessage id="composer.hints.instagram.mixed-direct-connect" />
            </div>
          )}
        </div>
      </PPDialog>
    </div>
  )
}

export default InstagramLocationPicker
